import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  User, Award, BookOpen, Building2, Bell, Save, Upload,
  Plus, Trash2, CheckCircle, AlertCircle, Loader2, Camera
} from 'lucide-react';
import toast from 'react-hot-toast';

const API_BASE    = process.env.REACT_APP_API_BASE    || 'https://api.abenaihr.com';
const API_KEY     = process.env.REACT_APP_API_KEY     || '';
const PROVIDER_ID = process.env.REACT_APP_PROVIDER_ID
  || localStorage.getItem('abena_provider_id')
  || 'PROVIDER-001';

const TABS = [
  { id: 'profile',      label: 'Profile',              icon: User },
  { id: 'licensing',    label: 'Licensing & Credentials', icon: Award },
  { id: 'education',    label: 'Education & Affiliations', icon: BookOpen },
  { id: 'practice',     label: 'Practice',             icon: Building2 },
  { id: 'notifications',label: 'Notifications',        icon: Bell },
];

const SPECIALTIES = [
  'Internal Medicine','Family Medicine','Pediatrics','Obstetrics & Gynecology',
  'Psychiatry','Neurology','Rheumatology','Oncology','Endocrinology',
  'Gastroenterology','Pain Management','Integrative Medicine',
  'Functional Medicine','Sports Medicine','Emergency Medicine','Other',
];

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'X-API-Key': API_KEY,
  'X-ABENA-Provider-Id': PROVIDER_ID,
});

// ── Reusable field components ────────────────────────────────────────────────

const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
  </div>
);

const Input = ({ ...props }) => (
  <input
    {...props}
    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm
               focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
               placeholder-gray-300"
  />
);

const Select = ({ children, ...props }) => (
  <select
    {...props}
    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm
               focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
    {children}
  </select>
);

const Textarea = ({ ...props }) => (
  <textarea
    {...props}
    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm
               focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none placeholder-gray-300"
  />
);

const Toggle = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-3 cursor-pointer">
    <div
      onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-gray-200'}`}>
      <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
    </div>
    <span className="text-sm text-gray-700">{label}</span>
  </label>
);

// ── Dynamic list editor (education, certs, affiliations) ─────────────────────
const ListEditor = ({ items = [], onChange, fields, addLabel }) => {
  const add = () => onChange([...items, Object.fromEntries(fields.map(f => [f.key, '']))]);
  const remove = i => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, key, val) => onChange(items.map((item, idx) => idx === i ? { ...item, [key]: val } : item));

  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="border border-gray-100 rounded-xl p-4 space-y-3 bg-gray-50">
          <div className="grid grid-cols-2 gap-3">
            {fields.map(f => (
              <div key={f.key} className={f.full ? 'col-span-2' : ''}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                <Input
                  value={item[f.key] || ''}
                  onChange={e => update(i, f.key, e.target.value)}
                  placeholder={f.placeholder || ''}
                />
              </div>
            ))}
          </div>
          <button onClick={() => remove(i)}
                  className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700">
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        </div>
      ))}
      <button onClick={add}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium">
        <Plus className="w-4 h-4" /> {addLabel}
      </button>
    </div>
  );
};

// ── Avatar / Logo uploader ───────────────────────────────────────────────────
const ImageUploader = ({ current, endpoint, label, onUploaded }) => {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const resp = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'X-API-Key': API_KEY, 'X-ABENA-Provider-Id': PROVIDER_ID },
        body: form,
      });
      if (!resp.ok) throw new Error((await resp.json()).detail || 'Upload failed');
      const data = await resp.json();
      toast.success(`${label} uploaded`);
      if (onUploaded) onUploaded(data);
    } catch (err) {
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className="w-20 h-20 rounded-2xl bg-gray-100 border-2 border-dashed border-gray-200 flex items-center justify-center overflow-hidden cursor-pointer hover:border-blue-400 transition-colors"
           onClick={() => ref.current?.click()}>
        {uploading
          ? <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          : current
            ? <img src={current} alt={label} className="w-full h-full object-cover" />
            : <Camera className="w-6 h-6 text-gray-400" />}
      </div>
      <div>
        <p className="text-sm font-medium text-gray-700">{label}</p>
        <button onClick={() => ref.current?.click()}
                className="mt-1 text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
          <Upload className="w-3 h-3" />
          {current ? 'Replace' : 'Upload'} {label}
        </button>
        <p className="text-xs text-gray-400 mt-0.5">JPG, PNG or GIF · max 5 MB</p>
      </div>
      <input ref={ref} type="file" accept="image/*" className="hidden"
             onChange={e => handleFile(e.target.files[0])} />
    </div>
  );
};

// ── Main Settings component ──────────────────────────────────────────────────
const Settings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);

  const [profile, setProfile] = useState({
    // Profile tab
    first_name: '', last_name: '', credentials: '', bio: '',
    languages: [], phone_direct: '', website: '',
    avatar_url: null,
    // Licensing tab
    specialty: '', npi: '', dea_number: '', state_license: '',
    cannabinoid_certified: false,
    // Education & Affiliations tab
    education: [],           // [{school, degree, year, field}]
    certifications: [],      // [{name, issuer, year, expires}]
    hospital_affiliations: [],// [{name, city, state, role}]
    // Practice tab
    clinic_name: '', logo_url: null,
    telehealth_enabled: false, accepting_patients: true,
    // Notifications (local pref only for now)
    notif_email: true, notif_sms: false, notif_push: true,
    notif_digest: true,
  });

  // Load profile on mount
  useEffect(() => {
    const load = async () => {
      try {
        const resp = await fetch(`${API_BASE}/provider/profile/${PROVIDER_ID}`, {
          headers: authHeaders(),
        });
        if (resp.ok) {
          const d = await resp.json();
          setProfile(prev => ({
            ...prev,
            first_name:            d.first_name            || '',
            last_name:             d.last_name             || '',
            credentials:           d.credentials           || '',
            bio:                   d.bio                   || '',
            languages:             d.languages             || [],
            phone_direct:          d.phone_direct          || d.phone || '',
            website:               d.website               || '',
            avatar_url:            d.avatar_url            || null,
            specialty:             d.specialty             || '',
            npi:                   d.npi                   || '',
            dea_number:            d.dea_number            || '',
            state_license:         d.state_license         || '',
            cannabinoid_certified: d.cannabinoid_certified || false,
            education:             d.education             || [],
            certifications:        d.certifications        || [],
            hospital_affiliations: d.hospital_affiliations || [],
            clinic_name:           d.clinic_name           || d.practice_name || '',
            logo_url:              d.logo_url              || null,
            telehealth_enabled:    d.telehealth_enabled    || false,
            accepting_patients:    d.accepting_patients    !== false,
          }));
        }
      } catch (e) {
        toast.error('Could not load profile — check API connection');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const set = (key, value) => setProfile(prev => ({ ...prev, [key]: value }));

  const saveProfile = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const resp = await fetch(`${API_BASE}/provider/profile/${PROVIDER_ID}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          first_name:            profile.first_name      || null,
          last_name:             profile.last_name       || null,
          credentials:           profile.credentials     || null,
          bio:                   profile.bio             || null,
          languages:             profile.languages.length ? profile.languages : null,
          phone_direct:          profile.phone_direct    || null,
          website:               profile.website         || null,
          specialty:             profile.specialty       || null,
          npi:                   profile.npi             || null,
          dea_number:            profile.dea_number      || null,
          state_license:         profile.state_license   || null,
          clinic_name:           profile.clinic_name     || null,
          telehealth_enabled:    profile.telehealth_enabled,
          accepting_patients:    profile.accepting_patients,
          education:             profile.education.length             ? profile.education             : null,
          certifications:        profile.certifications.length        ? profile.certifications        : null,
          hospital_affiliations: profile.hospital_affiliations.length ? profile.hospital_affiliations : null,
        }),
      });
      if (!resp.ok) throw new Error((await resp.json()).detail || 'Save failed');
      setSaved(true);
      toast.success('Profile saved');
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      toast.error(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Provider Settings</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage your profile, credentials, and practice information
        </p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-2xl p-1 overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}>
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab panels */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-6">

        {/* ── Profile tab ───────────────────────────────────────────── */}
        {activeTab === 'profile' && (<>
          <div>
            <h2 className="text-base font-semibold text-gray-900 mb-4">Personal Information</h2>
            <ImageUploader
              current={profile.avatar_url}
              endpoint={`/provider/profile/${PROVIDER_ID}/avatar`}
              label="Profile Photo"
              onUploaded={d => set('avatar_url', d.avatar_url || null)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="First Name">
              <Input value={profile.first_name} onChange={e => set('first_name', e.target.value)} placeholder="Jane" />
            </Field>
            <Field label="Last Name">
              <Input value={profile.last_name} onChange={e => set('last_name', e.target.value)} placeholder="Knox" />
            </Field>
          </div>
          <Field label="Post-Nominal Credentials" hint="e.g. MD, DO, PhD, MBA — appears after your name">
            <Input value={profile.credentials} onChange={e => set('credentials', e.target.value)} placeholder="MD, MBA" />
          </Field>
          <Field label="Bio / Professional Statement">
            <Textarea
              value={profile.bio} rows={4}
              onChange={e => set('bio', e.target.value)}
              placeholder="Brief professional biography visible to patients and colleagues..."
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Direct Phone">
              <Input value={profile.phone_direct} onChange={e => set('phone_direct', e.target.value)} placeholder="+1 (555) 000-0000" />
            </Field>
            <Field label="Website">
              <Input value={profile.website} onChange={e => set('website', e.target.value)} placeholder="https://yourpractice.com" />
            </Field>
          </div>
          <Field label="Languages Spoken" hint="Comma-separated — e.g. English, Spanish, French">
            <Input
              value={profile.languages.join(', ')}
              onChange={e => set('languages', e.target.value.split(',').map(l => l.trim()).filter(Boolean))}
              placeholder="English, Spanish"
            />
          </Field>
        </>)}

        {/* ── Licensing tab ─────────────────────────────────────────── */}
        {activeTab === 'licensing' && (<>
          <h2 className="text-base font-semibold text-gray-900">Licensing & Credentials</h2>
          <Field label="Specialty">
            <Select value={profile.specialty} onChange={e => set('specialty', e.target.value)}>
              <option value="">Select specialty...</option>
              {SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="NPI Number" hint="10-digit National Provider Identifier">
              <Input
                value={profile.npi}
                onChange={e => set('npi', e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="0000000000"
                maxLength={10}
              />
            </Field>
            <Field label="DEA Number" hint="DEA registration number">
              <Input
                value={profile.dea_number}
                onChange={e => set('dea_number', e.target.value.toUpperCase())}
                placeholder="AB1234563"
                maxLength={9}
              />
            </Field>
          </div>
          <Field label="State Medical License Number">
            <Input value={profile.state_license} onChange={e => set('state_license', e.target.value)} placeholder="State license #" />
          </Field>

          <div className="border-t border-gray-100 pt-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Board Certifications</h3>
            <ListEditor
              items={profile.certifications}
              onChange={v => set('certifications', v)}
              addLabel="Add certification"
              fields={[
                { key: 'name',   label: 'Certification Name', placeholder: 'e.g. ABIM Board Certified', full: true },
                { key: 'issuer', label: 'Issuing Board',      placeholder: 'e.g. ABIM, ABFM' },
                { key: 'year',   label: 'Year Issued',        placeholder: '2018' },
                { key: 'expires',label: 'Expires',            placeholder: '2028 (or leave blank)' },
              ]}
            />
          </div>

          <div className="border-t border-gray-100 pt-4">
            <Toggle
              checked={profile.cannabinoid_certified}
              onChange={v => set('cannabinoid_certified', v)}
              label="ABNA / Cannabinoid Medicine Certified"
            />
          </div>
        </>)}

        {/* ── Education & Affiliations tab ──────────────────────────── */}
        {activeTab === 'education' && (<>
          <div>
            <h2 className="text-base font-semibold text-gray-900 mb-1">Education & Training</h2>
            <p className="text-sm text-gray-500 mb-4">Medical school, residency, fellowship, and other degrees</p>
            <ListEditor
              items={profile.education}
              onChange={v => set('education', v)}
              addLabel="Add school / program"
              fields={[
                { key: 'school',  label: 'Institution',       placeholder: 'e.g. Johns Hopkins School of Medicine', full: true },
                { key: 'degree',  label: 'Degree / Program',  placeholder: 'MD, Residency, Fellowship...' },
                { key: 'field',   label: 'Field / Specialty', placeholder: 'Internal Medicine' },
                { key: 'year',    label: 'Year Completed',    placeholder: '2005' },
              ]}
            />
          </div>

          <div className="border-t border-gray-100 pt-6">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Hospital & Clinic Affiliations</h2>
            <p className="text-sm text-gray-500 mb-4">Current and active hospital or clinic affiliations</p>
            <ListEditor
              items={profile.hospital_affiliations}
              onChange={v => set('hospital_affiliations', v)}
              addLabel="Add affiliation"
              fields={[
                { key: 'name',  label: 'Hospital / Clinic Name', placeholder: 'e.g. Johns Hopkins Hospital', full: true },
                { key: 'role',  label: 'Role / Department',      placeholder: 'Attending Physician, Hospitalist...' },
                { key: 'city',  label: 'City',                   placeholder: 'Baltimore' },
                { key: 'state', label: 'State',                  placeholder: 'MD' },
              ]}
            />
          </div>
        </>)}

        {/* ── Practice tab ──────────────────────────────────────────── */}
        {activeTab === 'practice' && (<>
          <h2 className="text-base font-semibold text-gray-900">Practice Information</h2>
          <ImageUploader
            current={profile.logo_url}
            endpoint={`/provider/profile/${PROVIDER_ID}/logo`}
            label="Practice Logo"
            onUploaded={d => set('logo_url', d.logo_url || null)}
          />
          <Field label="Clinic / Practice Name">
            <Input value={profile.clinic_name} onChange={e => set('clinic_name', e.target.value)} placeholder="ABENA IHR Wellness Center" />
          </Field>
          <div className="space-y-3 border border-gray-100 rounded-xl p-4">
            <Toggle
              checked={profile.telehealth_enabled}
              onChange={v => set('telehealth_enabled', v)}
              label="Telehealth visits enabled"
            />
            <Toggle
              checked={profile.accepting_patients}
              onChange={v => set('accepting_patients', v)}
              label="Currently accepting new patients"
            />
          </div>
        </>)}

        {/* ── Notifications tab ─────────────────────────────────────── */}
        {activeTab === 'notifications' && (<>
          <h2 className="text-base font-semibold text-gray-900">Notification Preferences</h2>
          <p className="text-sm text-gray-500">Choose how you receive alerts for patient events, lab results, and appointments.</p>
          <div className="space-y-4 border border-gray-100 rounded-xl p-4">
            <Toggle checked={profile.notif_email}  onChange={v => set('notif_email', v)}  label="Email notifications" />
            <Toggle checked={profile.notif_sms}    onChange={v => set('notif_sms', v)}    label="SMS / text alerts" />
            <Toggle checked={profile.notif_push}   onChange={v => set('notif_push', v)}   label="In-app push notifications" />
            <Toggle checked={profile.notif_digest} onChange={v => set('notif_digest', v)} label="Daily patient digest email (7 AM)" />
          </div>
          <div className="bg-blue-50 rounded-xl p-4 text-sm text-blue-700">
            Critical alerts (abnormal labs, emergency flags) are always delivered regardless of preference.
          </div>
        </>)}

      </motion.div>

      {/* Save bar */}
      {activeTab !== 'notifications' && (
        <div className="mt-4 flex justify-end">
          <button
            onClick={saveProfile}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700
                       disabled:bg-gray-200 disabled:text-gray-400 text-white text-sm font-semibold transition-colors">
            {saving
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
              : saved
              ? <><CheckCircle className="w-4 h-4" /> Saved</>
              : <><Save className="w-4 h-4" /> Save Changes</>}
          </button>
        </div>
      )}
    </div>
  );
};

export default Settings;
